import React from 'react';
import KebabMenu, { KebabItem } from './KebabMenu';

// NavBar for the Trip detail AND Journey detail pages: history-aware back + the
// title shown once (design-handoff.md C.2 step 1). Given `menuItems`, the right
// side shows the shared ⋮ KebabMenu — each page owns its own action list, this
// component just places it. `actions` sit just left of the ⋮ (e.g. the
// journey page's desktop Share button). `onEdit` alone still renders a
// standalone "Edit" button (Unsorted list).

export interface TripDetailNavProps {
  title: string;
  onBack: () => void;
  /** When provided, renders an "Edit" action on the right (fallback surfaces). */
  onEdit?: () => void;
  /** The ⋮ overflow menu's rows. Takes precedence over onEdit. */
  menuItems?: KebabItem[];
  /** Accessible label for the ⋮ trigger, e.g. "Trip options". */
  menuAriaLabel?: string;
  /** Extra controls on the right, before the ⋮ menu (e.g. the Share button). */
  actions?: React.ReactNode;
  /** Small badge rendered immediately after the title (e.g. the MAIN tag). */
  titleBadge?: React.ReactNode;
}

const TripDetailNav: React.FC<TripDetailNavProps> = ({ title, onBack, onEdit, menuItems, menuAriaLabel = 'Options', actions, titleBadge }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
    <button
      onClick={onBack}
      aria-label="Back"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 36,
        height: 36,
        borderRadius: 999,
        border: 'none',
        background: '#ffffff',
        color: '#16241d',
        boxShadow: '0 1px 3px rgba(20,40,30,0.10)',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M9 2 L4 7 L9 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </button>
    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
      <h1
        style={{
          fontFamily: 'Fraunces, Georgia, serif',
          fontSize: 22,
          fontWeight: 600,
          color: '#16241d',
          margin: 0,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </h1>
      {titleBadge}
    </div>
    {actions && <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>{actions}</div>}
    {menuItems && menuItems.length > 0 ? (
      <div style={{ flexShrink: 0 }}>
        <KebabMenu ariaLabel={menuAriaLabel} items={menuItems} />
      </div>
    ) : onEdit ? (
      <button
        onClick={onEdit}
        style={{
          flexShrink: 0,
          padding: '7px 14px',
          borderRadius: 999,
          border: '1px solid rgba(20,40,30,0.15)',
          background: '#ffffff',
          color: '#157a55',
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        Edit
      </button>
    ) : null}
  </div>
);

export default TripDetailNav;
